import React from 'react';
import { Breadcrumbs, Link, Typography, Chip } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';

const BreadcrumbsNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, projectId, converterId, fileId, executionId } = useParams();

  // Parse path for names (could be improved by fetching names from API if needed)
  const crumbs = [
    { 
      label: 'Dashboard', 
      to: '/', 
      icon: <HomeIcon fontSize="small" /> 
    },
  ];

  if (companyId) {
    crumbs.push({ 
      label: 'Company', 
      to: `/company/${companyId}`, 
      icon: <BusinessIcon fontSize="small" />
    });
  }

  if (projectId) {
    const projectPath = companyId ? `/company/${companyId}/project/${projectId}` : `/project/${projectId}`;
    crumbs.push({ 
      label: 'Project', 
      to: projectPath, 
      icon: <AccountTreeIcon fontSize="small" />,
      isLegacy: !companyId
    });
  }

  // Legacy converter routes
  if (converterId) {
    crumbs.push({ 
      label: 'Converter', 
      to: `/project/${projectId}/converter/${converterId}`,
      icon: <AccountTreeIcon fontSize="small" />,
      isLegacy: true
    });
  }

  if (fileId) {
    crumbs.push({ 
      label: 'File', 
      to: location.pathname,
      icon: <DescriptionIcon fontSize="small" />,
      isLegacy: true
    });
  }

  // New execution routes
  if (executionId) {
    crumbs.push({ 
      label: 'Execution', 
      to: location.pathname,
      icon: <HistoryIcon fontSize="small" />
    });
  }

  // Client Users management
  if (companyId && location.pathname.includes('client-users')) {
    crumbs.push({ 
      label: 'Client Users', 
      to: location.pathname,
      icon: <PeopleIcon fontSize="small" />
    });
  }

  // User Management (system-wide)
  if (location.pathname === '/users') {
    crumbs.push({ 
      label: 'User Management', 
      to: location.pathname,
      icon: <PeopleIcon fontSize="small" />
    });
  }

  // Unmatched Emails (system-wide)
  if (location.pathname === '/unmatched-emails') {
    crumbs.push({ 
      label: 'Unmatched Emails', 
      to: location.pathname,
      icon: <MarkEmailUnreadIcon fontSize="small" />
    });
  }

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ my: 2 }}>
      {crumbs.map((crumb, idx) =>
        idx < crumbs.length - 1 ? (
          <Link
            key={crumb.to}
            color="inherit"
            underline="hover"
            onClick={() => navigate(crumb.to)}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
                padding: '4px 8px',
                margin: '-4px -8px'
              }
            }}
          >
            {crumb.icon}
            {crumb.label}
            {crumb.isLegacy && (
              <Chip 
                label="Legacy" 
                size="small" 
                color="warning" 
                variant="outlined"
                sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
              />
            )}
          </Link>
        ) : (
          <Typography 
            key={crumb.to} 
            color="text.primary"
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontWeight: 'medium'
            }}
          >
            {crumb.icon}
            {crumb.label}
            {crumb.isLegacy && (
              <Chip 
                label="Legacy" 
                size="small" 
                color="warning" 
                variant="outlined"
                sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
              />
            )}
          </Typography>
        )
      )}
    </Breadcrumbs>
  );
};

export default BreadcrumbsNav; 